import { Box, Grid, Stack, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwareRadioGroup } from '@/components/form/formAwareInputs/FormAwareRadioGroup';
import {
  FormAwareSelectInput,
  FormAwareSelectInputProps,
} from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFormContext } from '@/components/react-hook-form';
import { SuggestableFormAwareInput } from '@/modules/aiSuggestions/components/forms/SuggestableFormAwareInput';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';
import { validateNumberRange } from '@/utils/validators/validateNumberRange';

import { useTaxStatusAssetLocationOptions } from './hooks/useTaxStatusAssetLocationOptions';
import {
  GST_STATUS_ITEMS,
  MIXED_GST,
  PERSONAL_ACCOUNT_TAX_STATUS_ITEMS,
  TAX_STATUS_SUBFORM,
  TRUST_TAX_STATUS_ITEMS,
} from './TaxStatusSubform.constants';
import {
  Fields,
  SubformField,
  TaxStatusSubformProps,
} from './TaxStatusSubform.types';

export function UI({
  variant,
  selectedGrantorClientProfileIds,
}: TaxStatusSubformProps) {
  const {
    control,
    setValue,
    formState: { isDirty },
    setShouldBlockNavigation,
  } = useFormContext<Fields>();
  const { displayName: householdDisplayName, primaryClients } =
    useHouseholdDetailsContext();

  const estateInclusionStatusFieldName =
    `${TAX_STATUS_SUBFORM}.estateInclusionStatus` as const satisfies SubformField;
  const stateInEstateStatusFieldName =
    `${TAX_STATUS_SUBFORM}.stateInEstateStatus` as const satisfies SubformField;
  const taxStatusFieldName =
    `${TAX_STATUS_SUBFORM}.taxStatus` as const satisfies SubformField;
  const gstStatusFieldName =
    `${TAX_STATUS_SUBFORM}.gstStatus` as const satisfies SubformField;
  const inclusionRatioFieldName =
    `${TAX_STATUS_SUBFORM}.inclusionRatio` as const satisfies SubformField;

  const [estateInclusionStatus, gstStatus] = useWatch({
    name: [estateInclusionStatusFieldName, gstStatusFieldName],
    control,
  });

  const { assetLocationOptions } = useTaxStatusAssetLocationOptions({
    fieldNamePrefix: `${TAX_STATUS_SUBFORM}`,
  });

  // Default the entity-level state in-estate field to mirror the federal
  // value if it has not been explicitly set yet (mirrors how federal status
  // carries its default into the form).
  const stateInEstateStatusValue = useWatch({
    control,
    name: stateInEstateStatusFieldName,
  });
  useEffect(() => {
    if (stateInEstateStatusValue) return;
    if (!estateInclusionStatus) return;
    setValue(stateInEstateStatusFieldName, estateInclusionStatus, {
      shouldDirty: false,
    });
  }, [
    stateInEstateStatusValue,
    estateInclusionStatus,
    setValue,
    stateInEstateStatusFieldName,
  ]);

  // True iff this is a trust and a currently-selected grantor is not a
  // primary client of the household. Derived from live form values +
  // household context rather than persisted in the form, so the callout
  // below renders correctly regardless of tab-visit order.
  //
  // Gated on `variant === 'default'` (the trust variant) because
  // `selectedGrantorClientProfileIds` also carries owner IDs for non-trust
  // entities via `useSelectedPrincipals`, and the old `_hasNonClientGrantor`
  // field only existed in trust form shapes — this preserves that scope.
  const trustHasNonClientGrantor = useMemo(() => {
    if (variant !== 'default') return false;
    const selected = selectedGrantorClientProfileIds ?? [];
    if (selected.length === 0) return false;
    const primaryIds = new Set((primaryClients ?? []).map((c) => c.id));
    return !selected.every((id) => primaryIds.has(id));
  }, [variant, selectedGrantorClientProfileIds, primaryClients]);

  const isOutOfEstate = estateInclusionStatus === 'out-of-estate';

  // Respond to changes in the estate inclusion status to reset the GST status
  // when the user switches from out of estate to in estate
  useEffect(() => {
    if (!isOutOfEstate) {
      setValue(`${TAX_STATUS_SUBFORM}.gstStatus` as const, '');
    }
  }, [isOutOfEstate, setValue]);

  const isNonTrustEntityVariant = [
    'nonTrustEntity',
    'nonTrustEntityDefaultInEstate',
  ].includes(variant);

  const federalInEstateStatusValue = useWatch({
    control,
    name: `${TAX_STATUS_SUBFORM}.estateInclusionStatus`,
  });

  useEffect(() => {
    // Blocks navigation if the user manually toggles the in-estate status
    // radio buttons. This might be a little redundant, but otherwise,
    // the reset() in the useTaxStatusAssetLocationOptions hooks causes the
    // form to not block.
    if (isDirty) {
      setShouldBlockNavigation(true);
    }
  }, [federalInEstateStatusValue, isDirty, setShouldBlockNavigation]);

  return (
    <Grid container spacing={2} columns={12}>
      <Grid item sm={12}>
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            {variant == 'default' && (
              <FormAwareSelectInput<Fields>
                fieldName={taxStatusFieldName}
                label="Tax status"
                options={TRUST_TAX_STATUS_ITEMS}
                control={control}
              />
            )}
            {isNonTrustEntityVariant && (
              <FormAwareSelectInput<Fields>
                control={control}
                fieldName={
                  `${TAX_STATUS_SUBFORM}.nonTrustEntityTaxStatus` as const satisfies SubformField
                }
                label="Tax status"
                options={PERSONAL_ACCOUNT_TAX_STATUS_ITEMS}
              />
            )}
          </FormLayoutItem>
        </FormLayoutRow>
        <Card
          variant="filled"
          sx={{
            p: 2,
            pb: 4,
          }}
        >
          <Stack spacing={2}>
            <Typography variant="title-s">Asset location</Typography>
            <Stack direction="row" width="100%">
              <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box width="60px" />
                  <Typography width="60px" variant="body-xs">
                    In estate
                  </Typography>
                  <Typography width="86px" variant="body-xs">
                    Out of estate
                  </Typography>
                </Stack>
                {assetLocationOptions.map(({ label, fieldName, options }) => (
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    key={label}
                  >
                    <Typography width="60px" variant="body-xs">
                      {label}
                    </Typography>
                    <FormAwareRadioGroup<Fields>
                      sx={{
                        flexFlow: 'nowrap',
                      }}
                      labelSx={{
                        width: '84px', // 60px width + 24px padding
                      }}
                      row={true}
                      label={label}
                      hideLabel={true}
                      options={options}
                      fieldName={fieldName}
                      control={control}
                    />
                  </Stack>
                ))}
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  key="state"
                >
                  <Typography width="60px" variant="body-xs">
                    State
                  </Typography>
                  <FormAwareRadioGroup<Fields>
                    sx={{
                      flexFlow: 'nowrap',
                    }}
                    labelSx={{
                      width: '84px', // 60px width + 24px padding
                    }}
                    row={true}
                    label="State"
                    hideLabel={true}
                    options={[
                      { label: '', value: 'in-estate' },
                      { label: '', value: 'out-of-estate' },
                    ]}
                    fieldName={stateInEstateStatusFieldName}
                    control={control}
                  />
                </Stack>
              </Stack>
              <Box display="flex" flexGrow={1} maxWidth="48px" minWidth="8px" />
            </Stack>
            {isOutOfEstate && (
              <Card variant="filled">
                <Stack direction="row" spacing={2} alignItems="center" p={2}>
                  <FormLayoutRow sx={{ flex: '1 1 100%' }}>
                    <FormLayoutItem width={6}>
                      <SuggestableFormAwareInput<
                        Fields,
                        FormAwareSelectInputProps<Fields>
                      >
                        FormAwareElement={FormAwareSelectInput}
                        control={control}
                        fieldName={gstStatusFieldName}
                        label="GST status"
                        options={GST_STATUS_ITEMS}
                        suggestionKind={AiSuggestionKind.GstStatus}
                      />
                    </FormLayoutItem>
                    {gstStatus === MIXED_GST && (
                      <FormLayoutItem width={6}>
                        <FormAwareFormattedNumberInput<Fields>
                          control={control}
                          fieldName={inclusionRatioFieldName}
                          label="GST inclusion ratio"
                          required
                          decimalScale={4}
                          fixedDecimalScale
                          isDecimalJSInput
                          validation={{
                            minMax: validateNumberRange({
                              min: 0.0001,
                              max: 0.9999,
                            }),
                          }}
                        />
                      </FormLayoutItem>
                    )}
                  </FormLayoutRow>
                </Stack>
              </Card>
            )}
          </Stack>
        </Card>
        <FormLayoutRow
          sx={{
            alignItems: 'center',
          }}
        />
        {trustHasNonClientGrantor && (
          <FormLayoutRow>
            <FormLayoutItem>
              <Callout severity="info-high">
                This entity is{' '}
                {isOutOfEstate ? 'out of the estate of' : 'in the estate of'}{' '}
                {householdDisplayName}.
              </Callout>
            </FormLayoutItem>
          </FormLayoutRow>
        )}
      </Grid>
    </Grid>
  );
}
